import { createSelector } from '@reduxjs/toolkit';

import {
  BANK_LOCATION_KINDS,
  getTilaajavastuuProjectIdFromFolderId,
} from 'scenes/ProjectBank/utils/bankLocation';
import {
  getProjectIdFromProjectBankFormsFolderId,
  isProjectBankFormsFolderId,
} from 'scenes/ProjectBank/utils/projectBankForms';
import { findProjectRootFolder } from 'scenes/ProjectBank/utils/projectRootFolder';
import { selectSelectedProjectId } from 'store/projects/selectors/projectsSelectors';
import {
  selectInitialProjectBankPath,
  selectPrivateProjectBankItemsEntities,
  selectProjectFolderFilePermissions,
  selectProjectFolderFilePermissionsArray,
  selectSelectedPrivateProjectBankFolderId,
  selectSelectedProjectBankFolderId,
  selectSelectedProjectBankVisibilityMode,
  selectSelectedTilaajavastuuBusinessId,
  selectSelectedTilaajavastuuFolderProjectId,
} from './project-bank.selectors';

const selectProjectBankState = (state) => state.projectBank;

/** Whether initProjectBank has finished for the open project. */
export const selectIsProjectBankInitialised = createSelector(
  [selectProjectBankState],
  (state) => Boolean(state.isInitialised)
);

/** The request id of the initProjectBank that owns the slice ('' when none). */
export const selectProjectBankInitRequestId = createSelector(
  [selectProjectBankState],
  (state) => state.initRequestId || ''
);

const selectLoadedProjectBankFormsByProjectId = createSelector(
  [selectProjectBankState],
  (state) => state.loadedProjectBankFormsByProjectId || {}
);

/**
 * The root folder of the open (route) project among the loaded folders, or
 * null before the folders have loaded.
 */
export const selectProjectBankRootFolder = createSelector(
  [selectProjectFolderFilePermissionsArray, selectSelectedProjectId],
  (folders, projectId) => findProjectRootFolder(folders, projectId)
);

/**
 * Where the bank is, in the terms the URL carries (see utils/bankLocation):
 * the private bank's folder while the private bank is shown, otherwise the
 * tilaajavastuu view when one is open, otherwise the public folder. Null
 * until the bank has initialised or while nothing is selected.
 */
export const selectBankLocation = createSelector(
  [
    selectIsProjectBankInitialised,
    selectSelectedProjectBankVisibilityMode,
    selectSelectedTilaajavastuuFolderProjectId,
    selectSelectedTilaajavastuuBusinessId,
    selectSelectedProjectBankFolderId,
    selectSelectedPrivateProjectBankFolderId,
  ],
  (
    isInitialised,
    visibilityMode,
    tilaajavastuuProjectId,
    tilaajavastuuBusinessId,
    selectedFolderId,
    selectedPrivateFolderId
  ) => {
    if (!isInitialised) return null;

    if (visibilityMode === 'PRIVATE') {
      return {
        kind: BANK_LOCATION_KINDS.PRIVATE,
        folderId: selectedPrivateFolderId || '',
      };
    }

    if (tilaajavastuuProjectId) {
      return {
        kind: BANK_LOCATION_KINDS.TILAAJAVASTUU,
        projectId: tilaajavastuuProjectId,
        businessId: tilaajavastuuBusinessId || '',
      };
    }

    if (selectedFolderId) {
      return { kind: BANK_LOCATION_KINDS.PUBLIC, folderId: selectedFolderId };
    }

    return null;
  }
);

/** The kind resolveBankLocation answers when the query names nothing loaded. */
export const UNKNOWN_BANK_LOCATION = 'unknown';

/**
 * What a parsed query (utils/bankLocation parseBankLocationQuery) means
 * against the loaded data: a location of one of the three kinds, or
 * `{kind: 'unknown', reason}` when it cannot be honoured — a folder this
 * client does not hold, a private folder not among the project's items, a
 * tilaajavastuu view of a project whose folder is not loaded, or the forms
 * of a project not loaded yet (`reason: 'forms-not-loaded'`, with the
 * `formsProjectId` to load). No params at all means the project root.
 * @param {Object} state redux state
 * @param {Object} query parsed query
 * @param {string} projectId the route project
 * @returns {Object}
 */
export const resolveBankLocation = (state, query, projectId) => {
  const foldersById = selectProjectFolderFilePermissions(state);
  // The tree's root path (initProjectBank sets it to the route project's
  // root folder); a folder counts only under it — the loaded list can carry
  // a sibling project's folders.
  const rootPath = selectInitialProjectBankPath(state) || '';
  const isInTree = (folder) =>
    !rootPath ||
    folder.path === rootPath ||
    (typeof folder.path === 'string' && folder.path.startsWith(`${rootPath}/`));

  if (query.isPrivate) {
    const folderId = query.folderId || '';
    if (!folderId) {
      return { kind: BANK_LOCATION_KINDS.PRIVATE, folderId: '' };
    }
    const item = selectPrivateProjectBankItemsEntities(state)?.[folderId];
    if (
      item &&
      item.type === 'FOLDER' &&
      !item.deletedAt &&
      item.projectId === projectId
    ) {
      return { kind: BANK_LOCATION_KINDS.PRIVATE, folderId };
    }
    return {
      kind: UNKNOWN_BANK_LOCATION,
      reason: 'private-folder-not-found',
      fallback: { kind: BANK_LOCATION_KINDS.PRIVATE, folderId: '' },
    };
  }

  if (query.folderId) {
    const tilaajavastuuProjectId = getTilaajavastuuProjectIdFromFolderId(
      query.folderId
    );
    if (tilaajavastuuProjectId) {
      const projectFolder = findProjectRootFolder(
        Object.values(foldersById),
        tilaajavastuuProjectId
      );
      if (
        projectFolder &&
        projectFolder.businessId &&
        isInTree(projectFolder)
      ) {
        return {
          kind: BANK_LOCATION_KINDS.TILAAJAVASTUU,
          projectId: tilaajavastuuProjectId,
          businessId: query.businessId || '',
        };
      }
      return {
        kind: UNKNOWN_BANK_LOCATION,
        reason: 'tilaajavastuu-project-not-found',
      };
    }

    const folder = foldersById[query.folderId];
    if (folder && isInTree(folder)) {
      return { kind: BANK_LOCATION_KINDS.PUBLIC, folderId: folder.id };
    }
    if (folder) {
      return { kind: UNKNOWN_BANK_LOCATION, reason: 'folder-not-in-tree' };
    }

    if (isProjectBankFormsFolderId(query.folderId)) {
      const formsProjectId = getProjectIdFromProjectBankFormsFolderId(
        query.folderId
      );
      if (
        formsProjectId &&
        !selectLoadedProjectBankFormsByProjectId(state)[formsProjectId]
      ) {
        return {
          kind: UNKNOWN_BANK_LOCATION,
          reason: 'forms-not-loaded',
          formsProjectId,
        };
      }
    }

    return { kind: UNKNOWN_BANK_LOCATION, reason: 'folder-not-found' };
  }

  if (query.path) {
    const folder = selectProjectFolderFilePermissionsArray(state).find(
      (candidate) => candidate.path === query.path && isInTree(candidate)
    );
    if (folder) {
      return { kind: BANK_LOCATION_KINDS.PUBLIC, folderId: folder.id };
    }
    return { kind: UNKNOWN_BANK_LOCATION, reason: 'path-not-found' };
  }

  const root = findProjectRootFolder(
    selectProjectFolderFilePermissionsArray(state),
    projectId
  );
  if (root) {
    return { kind: BANK_LOCATION_KINDS.PUBLIC, folderId: root.id };
  }

  return { kind: UNKNOWN_BANK_LOCATION, reason: 'no-root' };
};
